import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Building2 } from "lucide-react";

export default function AdminLiquidez() {
  const [unidades, setUnidades] = useState([]);
  const [dadosLiquidez, setDadosLiquidez] = useState({});
  const [filtroTipo, setFiltroTipo] = useState("TODOS");
  const [ordenacao, setOrdenacao] = useState("liquidez_desc");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/unidades");
    setUnidades(data);
    // Carrega liquidez em paralelo
    const results = await Promise.all(
      data.map((u) => api.get(`/unidades/${u.id}/historico`).then((r) => [u.id, r.data]).catch(() => [u.id, null]))
    );
    const map = {};
    results.forEach(([id, d]) => { if (d) map[id] = d; });
    setDadosLiquidez(map);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = unidades.filter((u) => filtroTipo === "TODOS" || u.tipo === filtroTipo);
  const withData = filtered.map((u) => ({ u, d: dadosLiquidez[u.id] })).filter((x) => x.d);

  withData.sort((a, b) => {
    if (ordenacao === "liquidez_desc") return b.d.liquidez_ponto_percent - a.d.liquidez_ponto_percent;
    if (ordenacao === "liquidez_asc") return a.d.liquidez_ponto_percent - b.d.liquidez_ponto_percent;
    if (ordenacao === "turnover_desc") return b.d.turnover_total - a.d.turnover_total;
    return 0;
  });

  const totalLiquidez = withData.length > 0
    ? withData.reduce((sum, x) => sum + x.d.liquidez_ponto_percent, 0) / withData.length
    : 0;

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="label-tag mb-2">Liquidez dos pontos comerciais · Turnover histórico</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Liquidez</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="stat-block p-5 rounded-sm">
          <div className="label-tag">Liquidez média</div>
          <div className="stat-num text-3xl mt-2">{totalLiquidez.toFixed(1)}%</div>
        </div>
        <div className="stat-block p-5 rounded-sm">
          <div className="label-tag">Pontos analisados</div>
          <div className="stat-num text-3xl mt-2">{withData.length}</div>
        </div>
        <div className="stat-block p-5 rounded-sm">
          <div className="label-tag">Turnover médio</div>
          <div className="stat-num text-3xl mt-2">
            {withData.length > 0 ? (withData.reduce((s, x) => s + x.d.turnover_total, 0) / withData.length).toFixed(1) : "0"}
          </div>
        </div>
        <div className="stat-block p-5 rounded-sm">
          <div className="label-tag">Ativos</div>
          <div className="stat-num text-3xl mt-2 text-success">
            {withData.filter((x) => x.d.contratos.some((c) => c.status === "ATIVO")).length}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger data-testid="liq-tipo" className="w-52 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="TODOS">Todos os tipos</SelectItem>
            <SelectItem value="LOJA">Somente lojas</SelectItem>
            <SelectItem value="ESTANDE_CONTAINER">Somente estandes</SelectItem>
          </SelectContent>
        </Select>
        <Select value={ordenacao} onValueChange={setOrdenacao}>
          <SelectTrigger data-testid="liq-ordem" className="w-64 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="liquidez_desc">Maior liquidez primeiro</SelectItem>
            <SelectItem value="liquidez_asc">Menor liquidez primeiro</SelectItem>
            <SelectItem value="turnover_desc">Maior turnover</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground text-sm">Calculando liquidez das 65 unidades...</div>
      ) : (
        <Card className="p-6 rounded-sm border-border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-tag border-b border-border">
                  <th className="py-3">Unidade</th>
                  <th className="py-3">Turnover</th>
                  <th className="py-3">Dias ocupada (ano)</th>
                  <th className="py-3">Dias vaga (ano)</th>
                  <th className="py-3 text-right">Liquidez</th>
                </tr>
              </thead>
              <tbody>
                {withData.map(({ u, d }) => {
                  const cor = d.liquidez_ponto_percent >= 80 ? "text-success" : d.liquidez_ponto_percent >= 50 ? "text-warning" : "text-destructive";
                  return (
                    <tr key={u.id} data-testid={`liq-row-${u.numero_completo}`} className="border-b border-border/40 hover:bg-accent/40 transition-colors">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Building2 size={14} />
                          <div>
                            <div className="font-semibold">{u.numero_completo}</div>
                            <div className="text-xs text-muted-foreground">{u.tipo === "LOJA" ? "Loja" : "Estande"} · {u.metragem}m²</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 font-mono">{d.turnover_total}</td>
                      <td className="py-3 font-mono">{d.dias_ocupada_ano_atual}</td>
                      <td className="py-3 font-mono">{d.dias_vaga_ano_atual}</td>
                      <td className={`py-3 text-right stat-num text-xl ${cor}`}>{d.liquidez_ponto_percent}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
